// src/hooks/useMediaQuery.ts

import { useState, useEffect } from "react";

/**
 * React hook that subscribes to a CSS media query and returns whether it currently matches.
 *
 * @param query - A valid CSS media query string, e.g. "(min-width: 992px)".
 * @returns A boolean indicating whether the media query currently matches.
 *
 * @example
 * // Show a component only on large screens (992px+):
 * function MyComponent() {
 *   const isLgUp = useMediaQuery("(min-width: 992px)");
 *   return isLgUp
 *     ? <LargeLayout />
 *     : <SmallLayout />;
 * }
 *
 * @example
 * // Toggle a CSS class based on screen width:
 * function Sidebar() {
 *   const isMobile = useMediaQuery("(max-width: 767px)");
 *   return (
 *     <div className={isMobile ? "sidebar-mobile" : "sidebar-desktop"}>
 *       …
 *     </div>
 *   );
 * }
 */
export default function useMediaQuery(query: string): boolean {
    // Initialize state: if window is undefined (SSR), default to false
    const [matches, setMatches] = useState(() => {
        if (typeof window === "undefined") return false;
        return window.matchMedia(query).matches;
    });

    useEffect(() => {
        // Create a MediaQueryList for the given query
        const mql = window.matchMedia(query);

        // Event handler to update state when the query match changes
        const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);

        // Subscribe to the change event
        mql.addEventListener("change", onChange);

        // Clean up listener on unmount or when query changes
        return () => mql.removeEventListener("change", onChange);
    }, [query]);

    return matches;
}

